import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { usePrequeueStore } from '../../store/usePrequeueStore';

interface Props {
  children: ReactNode;
}

/**
 * Guard component for `/match` (and any post-setup route). Redirects to
 * `/app/setup` when:
 *  - `prequeueStore.state !== 'ready'` (and not enqueueing/enqueued), OR
 *  - `readyToken` is missing, OR
 *  - `readyToken.exp <= Date.now() / 1000` (TTL expired).
 *
 * A one-shot MUI `<Snackbar>` informs the user. The toast survives the
 * redirect by being mounted at the guard level (rendered alongside Navigate
 * via fragment).
 */
export function PrequeueGuard({ children }: Props): JSX.Element {
  const state = usePrequeueStore((s) => s.state);
  const readyToken = usePrequeueStore((s) => s.readyToken);
  const [toastOpen, setToastOpen] = useState<boolean>(false);

  const nowSec = Math.floor(Date.now() / 1000);
  const tokenExpired = readyToken !== null && readyToken.exp <= nowSec;
  const stateOk = state === 'ready' || state === 'enqueueing' || state === 'enqueued';
  const needsSetup = !stateOk || readyToken === null || tokenExpired;

  useEffect(() => {
    if (needsSetup) setToastOpen(true);
  }, [needsSetup]);

  if (needsSetup) {
    return (
      <>
        <Navigate to="/app/setup" replace />
        <Snackbar
          open={toastOpen}
          autoHideDuration={4000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="info" onClose={() => setToastOpen(false)}>
            Camera/mic setup required before matching.
          </Alert>
        </Snackbar>
      </>
    );
  }

  return <>{children}</>;
}
